/**
 * Compare California CAP GHG targets (california_cap_ghg_targets) to ghg_inventory_precalculated.
 * Supports multiple CAP rows per geofips and per-capita targets (goal_tco2e_per_capita / ghg_target_raw parsing).
 */

import { supabaseClient } from './supabase';
import {
  canonicalInventoryGeofipsKey,
  preferNewerPrecalcRow,
} from './ghgInventoryGeofipsCanonical';
import type { CaliforniaCapGhgTargetRow } from './californiaCapGhgTargetsService';
import {
  generatePolicyTimeSeriesData,
  POLICY_SERIES_YEAR_MIN,
  POLICY_SERIES_YEAR_MAX,
  type PolicyTimeSeriesResult,
  type TimeSeriesEmissionData,
} from './policyTimeSeriesService';
import { getDefaultScenario } from './policyImpactService';
import type { BuildingsBauScenario } from './buildingsBauPathways';

/** Match Policy Tool defaults: EMFAC 2021, Buildings BAU = current policy, default policy levers. */
const CAP_TABLE_POLICY_EMFAC: '2021' | '2025' = '2021';
const CAP_TABLE_BUILDINGS_BAU: BuildingsBauScenario = 'current_policy';
const POLICY_YEAR_MIN = POLICY_SERIES_YEAR_MIN;
const POLICY_YEAR_MAX = POLICY_SERIES_YEAR_MAX;

export const LATEST_INVENTORY_YEAR = 2023;
export const FIRST_INVENTORY_YEAR = 2005;
/** Years we expect when precalc is complete for statewide comparison (2005–2023 inclusive). */
export const EXPECTED_PRECALC_YEAR_COUNT = LATEST_INVENTORY_YEAR - FIRST_INVENTORY_YEAR + 1;
const PAGE = 800;

export type CapTargetInventoryStatus =
  | 'yes'
  | 'partial'
  | 'no';

/** Sector totals (MT CO₂e) from one precalc row after dedupe */
export interface PrecalcSectorSnapshot {
  year: number;
  totalMt: number | null;
  ldvMt: number | null;
  hdvMt: number | null;
  elecMt: number | null;
  ngMt: number | null;
  wasteMt: number | null;
  offroadMt: number | null;
  agMt: number | null;
}

export interface CapTargetProgressDetail {
  capRow: CaliforniaCapGhgTargetRow;
  /** Total emissions by year from precalc (prefer newest calculation_date per year). */
  emissionsByYear: Record<number, number>;
  /** From ghg_inventory_precalculated: min year present after dedupe. */
  precalcYearMin: number | null;
  /** From ghg_inventory_precalculated: max year present. */
  precalcYearMax: number | null;
  /** Number of distinct inventory years in precalc for this jurisdiction. */
  precalcYearCount: number;
  /** Human-readable span, e.g. "2005–2023 (19 yrs)". */
  precalcSpanLabel: string;
  /** Latest calculation_date seen across fetched precalc rows (ISO string); freshness of merged data. */
  precalcDataAsOf: string | null;
  inventoryStatus: CapTargetInventoryStatus;
  /** Interpretable goal: X% below baseline_year by target_year when percent_below set. */
  goalSummary: string;
  /** Parsed per-capita goal (tonnes CO₂e per person) when applicable. */
  goalTonnesCo2ePerCapita: number | null;
  /** True when comparison uses per-capita goal + population vs inventory. */
  usesPerCapitaGoal: boolean;
  /** Goal emissions (MT CO₂e) at target_year under % goal; null if not computable or superseded by per-capita goal. */
  goalEmissionsMt: number | null;
  /** Precalc total at baseline_year when available. */
  baselineInventoryMt: number | null;
  /** Precalc total at target_year when target_year ≤ LATEST_INVENTORY_YEAR. */
  emissionsAtTargetYearMt: number | null;
  /**
   * Target met: inventory at deadline when target year ≤ latest inventory year; otherwise Policy Tool projected total vs goal.
   */
  targetMet: boolean | null;
  /** @deprecated Inventory linear path removed; always null. */
  linearExpectedLatestMt: number | null;
  /** Precalc total at LATEST_INVENTORY_YEAR. */
  latestInventoryMt: number | null;
  /** @deprecated Use onTrackPolicyPath. */
  onTrackLinearPath: boolean | null;
  /** @deprecated Use policy reduction gap. */
  linearGapMt: number | null;
  /** population.year for LATEST_INVENTORY_YEAR when available */
  populationLatest: number | null;
  /** population at CAP baseline_year when available */
  populationBaseline: number | null;
  /** population at target_year when target_year ≤ LATEST_INVENTORY_YEAR */
  populationGoalYear: number | null;
  /** total_emissions / population (tonnes CO₂e per resident), latest inventory year */
  perCapitaLatestTonnes: number | null;
  /** Same metric at baseline reference year */
  perCapitaBaselineTonnes: number | null;
  /** Inventory total at target year ÷ population that year (t CO₂e per resident); null if missing data. */
  actualPerCapitaAtTargetYearTonnes: number | null;
  /** @deprecated Always null (linear path removed). */
  linearExpectedPerCapitaLatestTonnes: number | null;
  /** @deprecated Always null. */
  linearGapPerCapitaTonnes: number | null;
  /**
   * At CAP target year vs goal: inventory actual or Policy Tool projected vs goal level.
   * Positive = emissions above the goal (worse).
   */
  pctVsGoalAtTargetYear: number | null;
  /**
   * CAP-required reduction % minus Policy Tool achieved reduction % (percentage points).
   * Positive = short of the required reduction (e.g. need 100% reduction but policies achieve 36% → ~+64 pp).
   */
  policyReductionGapPp: number | null;
  /** Policy Tool baseline trajectory total (MT) at CAP baseline year. */
  policyBaselineMtPolicySeries: number | null;
  /** Policy Tool projected community total (MT) at CAP target year (default scenario). */
  policyProjectedMtAtTargetYear: number | null;
  /** (baseline − projected) / baseline × 100 using Policy Tool series at CAP baseline & target years. */
  policyAchievedReductionPct: number | null;
  /** CAP percent_below (or 100 for carbon-neutral style mass goals when % missing). */
  capRequiredReductionPct: number | null;
  /** Policy Tool projected t/cap at target year when population known. */
  policyProjectedPerCapAtTarget: number | null;
  /** Achieved policy reduction meets CAP requirement (within ~1 pp). Mass % goals; per-cap uses goal vs projected t/cap. */
  onTrackPolicyPath: boolean | null;
  /** @deprecated Always null; replaced by policyReductionGapPp. */
  pctVsLinearAtLatestYear: number | null;
  /** Sector mix for latest inventory year (expandable UI / CSV) */
  sectorsLatest: PrecalcSectorSnapshot | null;
  /** Sector mix at baseline inventory year when present */
  sectorsBaseline: PrecalcSectorSnapshot | null;
  /** True if linear path missed or target not met — useful for highlighting sector drilldown */
  phase2Flagged: boolean;
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** ((actual / reference) − 1) × 100; null if missing or reference ≤ 0. */
export function pctActualVersusReference(actual: number | null, reference: number | null): number | null {
  if (actual == null || reference == null) return null;
  if (!Number.isFinite(actual) || !Number.isFinite(reference) || reference <= 0) return null;
  return ((actual / reference) - 1) * 100;
}

function clampPolicyYear(y: number): number {
  return Math.min(POLICY_YEAR_MAX, Math.max(POLICY_YEAR_MIN, Math.round(y)));
}

function totalEmissionsPolicyYear(
  series: TimeSeriesEmissionData[] | undefined,
  year: number | null
): number | null {
  if (series == null || year == null || !Number.isFinite(year)) return null;
  const y = clampPolicyYear(year);
  const row = series.find((d) => d.year === y);
  const t = row?.totalEmissions;
  if (t == null || !Number.isFinite(t)) return null;
  return t;
}

async function fetchPolicySeriesByLocation(
  locationNames: string[]
): Promise<Map<string, PolicyTimeSeriesResult | null>> {
  const out = new Map<string, PolicyTimeSeriesResult | null>();
  const unique = Array.from(new Set(locationNames.map((s) => String(s || '').trim()).filter(Boolean)));
  const CHUNK = 6;
  const scenario = getDefaultScenario();
  for (let i = 0; i < unique.length; i += CHUNK) {
    const chunk = unique.slice(i, i + CHUNK);
    await Promise.all(
      chunk.map(async (loc) => {
        try {
          const ts = await generatePolicyTimeSeriesData(
            loc,
            scenario,
            CAP_TABLE_POLICY_EMFAC,
            CAP_TABLE_BUILDINGS_BAU
          );
          out.set(loc, ts);
        } catch (e) {
          console.warn(`capTargetsProgressService: Policy Tool series failed for "${loc}"`, e);
          out.set(loc, null);
        }
      })
    );
  }
  return out;
}

function sectorSnapshotFromRow(year: number, row: Record<string, unknown>): PrecalcSectorSnapshot {
  // Use wastewater-inclusive total where available (null means wastewater not yet calculated)
  const totalWithWw = row.total_emissions_with_wastewater != null
    ? num(row.total_emissions_with_wastewater)
    : num(row.total_emissions);
  return {
    year,
    totalMt: totalWithWw,
    ldvMt: num(row.ldv_emissions),
    hdvMt: num(row.hdv_emissions),
    elecMt: num(row.elec_total_emissions),
    ngMt: num(row.ng_total_emissions),
    wasteMt: num(row.waste_emissions),
    offroadMt: num(row.offroad_emissions),
    agMt: num(row.agriculture_emissions),
  };
}

/**
 * Dedupe precalc rows by canonical geofips + year (newest calculation_date wins).
 * Returns map canon → year → winning row (full row for sector extraction).
 */
function dedupePrecalcRows(rows: Record<string, unknown>[]): Map<string, Map<number, Record<string, unknown>>> {
  const keyed = new Map<string, Map<number, Record<string, unknown>>>();

  for (const raw of rows) {
    const gfRaw = raw.geofips;
    const canon = canonicalInventoryGeofipsKey(gfRaw);
    if (!canon) continue;
    const yr = num(raw.year);
    if (yr == null || yr < FIRST_INVENTORY_YEAR || yr > LATEST_INVENTORY_YEAR) continue;

    let inner = keyed.get(canon);
    if (!inner) {
      inner = new Map();
      keyed.set(canon, inner);
    }
    const prev = inner.get(yr);
    if (!prev) {
      inner.set(yr, raw as Record<string, unknown>);
    } else {
      inner.set(yr, preferNewerPrecalcRow(prev as never, raw as never));
    }
  }
  return keyed;
}

function totalsFromDeduped(
  keyed: Map<string, Map<number, Record<string, unknown>>>
): Map<string, Record<number, number>> {
  const out = new Map<string, Record<number, number>>();
  for (const [cfips, yrMap] of Array.from(keyed.entries())) {
    const rec: Record<number, number> = {};
    for (const [y, row] of Array.from(yrMap.entries())) {
      // Prefer wastewater-inclusive total for consistency with KPI headline and sector snapshots.
      const r = row as { total_emissions?: unknown; total_emissions_with_wastewater?: unknown };
      const t = r.total_emissions_with_wastewater != null
        ? num(r.total_emissions_with_wastewater)
        : num(r.total_emissions);
      if (t != null && t >= 0) rec[y] = t;
    }
    out.set(cfips, rec);
  }
  return out;
}

function sectorsFromDeduped(
  keyed: Map<string, Map<number, Record<string, unknown>>>
): Map<string, Map<number, PrecalcSectorSnapshot>> {
  const out = new Map<string, Map<number, PrecalcSectorSnapshot>>();
  for (const [cfips, yrMap] of Array.from(keyed.entries())) {
    const inner = new Map<number, PrecalcSectorSnapshot>();
    for (const [y, row] of Array.from(yrMap.entries())) {
      inner.set(y, sectorSnapshotFromRow(y, row));
    }
    out.set(cfips, inner);
  }
  return out;
}

async function fetchAllCapTargets(): Promise<CaliforniaCapGhgTargetRow[]> {
  const all: CaliforniaCapGhgTargetRow[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await supabaseClient
      .from('california_cap_ghg_targets')
      .select('*')
      .not('target_year', 'is', null)
      .order('location_name', { ascending: true })
      .order('target_year', { ascending: true })
      .order('target_source', { ascending: true })
      .range(from, from + PAGE - 1);

    if (error) {
      console.warn('capTargetsProgressService: california_cap_ghg_targets', error.message);
      return all;
    }
    const batch = (data ?? []) as CaliforniaCapGhgTargetRow[];
    all.push(...batch);
    if (batch.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

function bumpMaxIsoDate(current: string | null, candidate: unknown): string | null {
  if (candidate == null) return current;
  const s = String(candidate).trim();
  if (!s) return current;
  if (!current || s > current) return s;
  return current;
}

const PRECALC_SECTOR_SELECT =
  'geofips,year,total_emissions,total_emissions_with_wastewater,calculation_date,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions,wastewater_emissions';

async function fetchPrecalcForGeofipsList(geofipsList: number[]): Promise<{
  seriesByCanon: Map<string, Record<number, number>>;
  sectorsByCanonYear: Map<string, Map<number, PrecalcSectorSnapshot>>;
  precalcAsOfByCanon: Map<string, string | null>;
}> {
  const mergedKeyed = new Map<string, Map<number, Record<string, unknown>>>();
  const precalcAsOfByCanon = new Map<string, string | null>();
  const chunkSize = 80;

  const mergeKeyed = (chunkKeyed: Map<string, Map<number, Record<string, unknown>>>) => {
    for (const [canon, yrMap] of Array.from(chunkKeyed.entries())) {
      let dest = mergedKeyed.get(canon);
      if (!dest) {
        mergedKeyed.set(canon, new Map(yrMap));
        continue;
      }
      for (const [y, row] of Array.from(yrMap.entries())) {
        const prev = dest.get(y);
        if (!prev) dest.set(y, row);
        else dest.set(y, preferNewerPrecalcRow(prev as never, row as never));
      }
    }
  };

  for (let i = 0; i < geofipsList.length; i += chunkSize) {
    const chunk = geofipsList.slice(i, i + chunkSize);
    const { data, error } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select(PRECALC_SECTOR_SELECT)
      .in('geofips', chunk)
      .gte('year', FIRST_INVENTORY_YEAR)
      .lte('year', LATEST_INVENTORY_YEAR);

    if (error) {
      console.warn('capTargetsProgressService: ghg_inventory_precalculated', error.message);
      continue;
    }
    const rows = (data ?? []) as Record<string, unknown>[];
    for (const raw of rows) {
      const canon = canonicalInventoryGeofipsKey(raw.geofips);
      if (!canon) continue;
      precalcAsOfByCanon.set(
        canon,
        bumpMaxIsoDate(precalcAsOfByCanon.get(canon) ?? null, raw.calculation_date)
      );
    }
    mergeKeyed(dedupePrecalcRows(rows));
  }

  const seriesByCanon = totalsFromDeduped(mergedKeyed);
  const sectorsByCanonYear = sectorsFromDeduped(mergedKeyed);

  return { seriesByCanon, sectorsByCanonYear, precalcAsOfByCanon };
}

async function fetchPopulationForGeofipsYears(
  geofipsList: number[],
  years: number[]
): Promise<Map<number, Record<number, number>>> {
  const uniqYears = Array.from(new Set(years.filter((y) => Number.isFinite(y) && y >= 1990 && y <= 2060)));
  const out = new Map<number, Record<number, number>>();
  if (uniqYears.length === 0 || geofipsList.length === 0) return out;

  const chunkSize = 120;
  for (let i = 0; i < geofipsList.length; i += chunkSize) {
    const chunk = geofipsList.slice(i, i + chunkSize);
    const { data, error } = await supabaseClient
      .from('population')
      .select('geofips,year,population')
      .in('geofips', chunk)
      .in('year', uniqYears);

    if (error) {
      console.warn('capTargetsProgressService: population', error.message);
      continue;
    }
    for (const raw of data ?? []) {
      const gf = num((raw as { geofips?: unknown }).geofips);
      const yr = num((raw as { year?: unknown }).year);
      const pop = num((raw as { population?: unknown }).population);
      if (gf == null || yr == null || pop == null || pop <= 0) continue;
      let rec = out.get(gf);
      if (!rec) {
        rec = {};
        out.set(gf, rec);
      }
      rec[yr] = pop;
    }
  }
  return out;
}

function percentBelowNumber(row: CaliforniaCapGhgTargetRow): number | null {
  const p = num(row.percent_below);
  if (p == null) return null;
  return Math.max(0, Math.min(100, p));
}

function capRequiredReductionPercent(
  cap: CaliforniaCapGhgTargetRow,
  usesPerCapitaGoal: boolean
): number | null {
  if (usesPerCapitaGoal) return null;
  const p = percentBelowNumber(cap);
  if (p != null) return p;
  const ts = String(cap.target_source || '').toLowerCase();
  if (ts.includes('carbon') && ts.includes('neutral')) return 100;
  return null;
}

function goalEmissionsMt(
  baselineMt: number,
  pctBelow: number | null
): number | null {
  if (pctBelow == null || !Number.isFinite(baselineMt)) return null;
  return baselineMt * (1 - pctBelow / 100);
}

function perCapitaTonnes(totalMt: number | null, population: number | null): number | null {
  if (totalMt == null || !Number.isFinite(totalMt) || population == null || population <= 0) return null;
  return totalMt / population;
}

/** Tonnes CO₂e per person — "M" in MTCO2e is metric tonne, not million, unless "million" appears in text. */
export function parseGoalTonnesPerCapitaFromText(text: string | null | undefined): number | null {
  if (text == null || !String(text).trim()) return null;
  const s = String(text);
  const mentionsPerCap =
    /\bper\s+capita\b/i.test(s) ||
    /\/\s*capita/i.test(s) ||
    /\/\s*person/i.test(s) ||
    /\bper\s+person\b/i.test(s);
  if (!mentionsPerCap) return null;
  const millionWord = /\bmillion\b/i.test(s);
  const re =
    /(\d+(?:\.\d+)?)\s*(million\s*)?(MTCO2e|mt\s*CO2e?|metric\s+tons?\s+(?:CO2|CO₂)?(?:e)?|t\s*CO2e?)/gi;
  const m = re.exec(s);
  if (!m) return null;
  let v = parseFloat(m[1]);
  if (!Number.isFinite(v) || v < 0) return null;
  const millionPrefix = !!(m[2] && String(m[2]).trim()) || millionWord;
  if (millionPrefix) v *= 1e6;
  if (!millionPrefix && v > 800) return null;
  return v;
}

/** Prefer DB column goal_tco2e_per_capita; else parse ghg_target_raw (Fairfield-style "6 MTCO2e per capita …"). */
export function extractGoalTonnesPerCapita(cap: CaliforniaCapGhgTargetRow): number | null {
  const ext = cap as CaliforniaCapGhgTargetRow & { goal_tco2e_per_capita?: unknown; ghg_target_raw?: unknown };
  const fromCol = num(ext.goal_tco2e_per_capita);
  if (fromCol != null && fromCol >= 0 && fromCol <= 800) return fromCol;
  const raw = ext.ghg_target_raw;
  if (raw != null && String(raw).trim()) {
    const parsed = parseGoalTonnesPerCapitaFromText(String(raw));
    if (parsed != null && parsed <= 800) return parsed;
  }
  return null;
}

/** Stable React key / expand id when multiple CAP rows exist per jurisdiction. */
export function capRowStableKey(cap: CaliforniaCapGhgTargetRow): string {
  const ext = cap as CaliforniaCapGhgTargetRow & { id?: unknown };
  const rid = num(ext.id);
  if (rid != null && rid > 0) return `id:${Math.round(rid)}`;
  const gf = Number(cap.geofips);
  const ty = cap.target_year != null ? Math.round(Number(cap.target_year)) : 'na';
  const ts = cap.target_source ?? '';
  const pb = cap.percent_below != null && cap.percent_below !== '' ? String(cap.percent_below) : '';
  const raw = (cap as { ghg_target_raw?: string | null }).ghg_target_raw;
  const rawH = raw != null ? String(raw).slice(0, 24) : '';
  return `${gf}:${ty}:${ts}:${pb}:${rawH}`;
}

export function buildCapTargetProgressDetail(
  cap: CaliforniaCapGhgTargetRow,
  emissionsByYear: Record<number, number> | undefined,
  precalcDataAsOf: string | null,
  populationByYear: Record<number, number> | undefined,
  sectorsByYear: Map<number, PrecalcSectorSnapshot> | undefined,
  policyTimeSeries: PolicyTimeSeriesResult | null
): CapTargetProgressDetail {
  const pop = populationByYear ?? {};
  const by = emissionsByYear ?? {};
  const yearKeys = Object.keys(by)
    .map((k) => Number(k))
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);
  const precalcYearCount = yearKeys.length;
  const precalcYearMin = precalcYearCount ? yearKeys[0] : null;
  const precalcYearMax = precalcYearCount ? yearKeys[yearKeys.length - 1] : null;
  const precalcSpanLabel =
    precalcYearCount === 0
      ? '—'
      : `${precalcYearMin}–${precalcYearMax} (${precalcYearCount} yr${precalcYearCount === 1 ? '' : 's'})`;

  let inventoryStatus: CapTargetInventoryStatus = 'no';
  if (precalcYearCount > 0) {
    inventoryStatus =
      precalcYearCount >= EXPECTED_PRECALC_YEAR_COUNT ? 'yes' : 'partial';
  }

  const pct = percentBelowNumber(cap);
  const baselineYear = cap.baseline_year != null ? Math.round(Number(cap.baseline_year)) : null;
  const targetYear = cap.target_year != null ? Math.round(Number(cap.target_year)) : null;

  const baselineInventoryMt =
    baselineYear != null && Number.isFinite(baselineYear) ? num(by[baselineYear]) : null;

  const goalPerCapTonnes = extractGoalTonnesPerCapita(cap);
  const usesPerCapitaGoal = goalPerCapTonnes != null;

  let goalSummary = '—';
  if (usesPerCapitaGoal && goalPerCapTonnes != null && targetYear != null) {
    const decimals = goalPerCapTonnes < 10 ? 2 : 1;
    goalSummary = `≤ ${goalPerCapTonnes.toFixed(decimals)} t CO₂e per capita by ${targetYear}`;
    if (cap.target_source === 'carbon_neutral') goalSummary += ' (carbon neutral)';
  } else if (pct != null && baselineYear != null && targetYear != null) {
    goalSummary = `${pct}% below ${baselineYear} levels by ${targetYear}`;
    if (cap.target_source === 'carbon_neutral') {
      goalSummary += ' (carbon neutral)';
    }
  } else if (targetYear != null) {
    goalSummary = `Target year ${targetYear}` + (pct == null ? ' (no % in database)' : '');
  }

  let goalEmissionsMtOut: number | null = null;
  if (!usesPerCapitaGoal && pct != null && baselineInventoryMt != null && baselineInventoryMt >= 0) {
    goalEmissionsMtOut = goalEmissionsMt(baselineInventoryMt, pct);
  }

  let emissionsAtTargetYearMt: number | null = null;
  if (targetYear != null && targetYear <= LATEST_INVENTORY_YEAR) {
    emissionsAtTargetYearMt = num(by[targetYear]);
  }

  let actualPerCapitaAtTargetYearTonnes: number | null = null;
  if (targetYear != null && targetYear <= LATEST_INVENTORY_YEAR) {
    const popTy = num(pop[targetYear]);
    if (emissionsAtTargetYearMt != null && popTy != null && popTy > 0) {
      actualPerCapitaAtTargetYearTonnes = emissionsAtTargetYearMt / popTy;
    }
  }

  /** 1 Mt tolerance on mass goals; per-capita tolerance in t CO₂e per person */
  const GOAL_TOL_MT = 1;
  const GOAL_TOL_PER_CAP_TCO2E = 0.08;

  let policyBaselineMtPolicySeries: number | null = null;
  let policyProjectedMtAtTargetYear: number | null = null;
  let policyProjectedPerCapAtTarget: number | null = null;
  let policyAchievedReductionPct: number | null = null;

  const pts = policyTimeSeries;

  if (pts && baselineYear != null && Number.isFinite(baselineYear) && targetYear != null && Number.isFinite(targetYear)) {
    policyBaselineMtPolicySeries = totalEmissionsPolicyYear(pts.baselineData, baselineYear);
    policyProjectedMtAtTargetYear = totalEmissionsPolicyYear(pts.projectedData, targetYear);
    const tyK = clampPolicyYear(targetYear);
    const popTarget =
      num(pop[tyK]) ??
      num(pop[targetYear]) ??
      null;
    if (policyProjectedMtAtTargetYear != null && popTarget != null && popTarget > 0) {
      policyProjectedPerCapAtTarget = policyProjectedMtAtTargetYear / popTarget;
    }
    if (
      policyBaselineMtPolicySeries != null &&
      policyBaselineMtPolicySeries > 0 &&
      policyProjectedMtAtTargetYear != null &&
      Number.isFinite(policyProjectedMtAtTargetYear)
    ) {
      policyAchievedReductionPct =
        ((policyBaselineMtPolicySeries - policyProjectedMtAtTargetYear) / policyBaselineMtPolicySeries) * 100;
    }
  }

  const capRequiredReductionPct = capRequiredReductionPercent(cap, usesPerCapitaGoal);
  let policyReductionGapPp: number | null = null;
  if (!usesPerCapitaGoal && capRequiredReductionPct != null && policyAchievedReductionPct != null) {
    policyReductionGapPp = capRequiredReductionPct - policyAchievedReductionPct;
  }

  let targetMet: boolean | null = null;
  if (
    usesPerCapitaGoal &&
    goalPerCapTonnes != null &&
    targetYear != null &&
    targetYear <= LATEST_INVENTORY_YEAR
  ) {
    const actualPerCap = actualPerCapitaAtTargetYearTonnes;
    if (actualPerCap != null) {
      targetMet = actualPerCap <= goalPerCapTonnes + GOAL_TOL_PER_CAP_TCO2E;
    }
  } else if (
    usesPerCapitaGoal &&
    goalPerCapTonnes != null &&
    targetYear != null &&
    targetYear > LATEST_INVENTORY_YEAR &&
    policyProjectedPerCapAtTarget != null
  ) {
    targetMet = policyProjectedPerCapAtTarget <= goalPerCapTonnes + GOAL_TOL_PER_CAP_TCO2E;
  } else if (
    !usesPerCapitaGoal &&
    goalEmissionsMtOut != null &&
    emissionsAtTargetYearMt != null &&
    targetYear != null &&
    targetYear <= LATEST_INVENTORY_YEAR
  ) {
    targetMet = emissionsAtTargetYearMt <= goalEmissionsMtOut + GOAL_TOL_MT;
  } else if (
    !usesPerCapitaGoal &&
    goalEmissionsMtOut != null &&
    targetYear != null &&
    targetYear > LATEST_INVENTORY_YEAR &&
    policyProjectedMtAtTargetYear != null
  ) {
    targetMet = policyProjectedMtAtTargetYear <= goalEmissionsMtOut + GOAL_TOL_MT;
  }

  const latestInventoryMt: number | null = num(by[LATEST_INVENTORY_YEAR]);
  const linearExpectedLatestMt: number | null = null;
  const linearExpectedPerCapitaLatestTonnes: number | null = null;
  const linearGapPerCapitaTonnes: number | null = null;
  const onTrackLinearPath: boolean | null = null;
  const linearGapMt: number | null = null;

  const populationLatest = num(pop[LATEST_INVENTORY_YEAR]);
  const populationBaseline =
    baselineYear != null && Number.isFinite(baselineYear) ? num(pop[baselineYear]) : null;
  const populationGoalYear = targetYear != null ? num(pop[targetYear]) : null;

  const perCapitaLatestTonnes = perCapitaTonnes(latestInventoryMt, populationLatest);
  const perCapitaBaselineTonnes = perCapitaTonnes(baselineInventoryMt, populationBaseline);

  let sectorsLatest: PrecalcSectorSnapshot | null = null;
  let sectorsBaseline: PrecalcSectorSnapshot | null = null;
  if (sectorsByYear && sectorsByYear.size > 0) {
    sectorsLatest = sectorsByYear.get(LATEST_INVENTORY_YEAR) ?? null;
    if (baselineYear != null && Number.isFinite(baselineYear)) {
      sectorsBaseline = sectorsByYear.get(baselineYear) ?? null;
    }
  }

  let onTrackPolicyPath: boolean | null = null;
  if (!usesPerCapitaGoal && capRequiredReductionPct != null && policyAchievedReductionPct != null) {
    onTrackPolicyPath = policyAchievedReductionPct >= capRequiredReductionPct - 1;
  } else if (
    usesPerCapitaGoal &&
    goalPerCapTonnes != null &&
    policyProjectedPerCapAtTarget != null
  ) {
    onTrackPolicyPath = policyProjectedPerCapAtTarget <= goalPerCapTonnes + GOAL_TOL_PER_CAP_TCO2E;
  } else if (targetMet !== null && targetYear != null && targetYear > LATEST_INVENTORY_YEAR) {
    onTrackPolicyPath = targetMet;
  }

  const phase2Flagged = targetMet === false || onTrackPolicyPath === false;

  let pctVsGoalAtTargetYear: number | null = null;
  if (usesPerCapitaGoal && goalPerCapTonnes != null) {
    const actualPc =
      targetYear != null && targetYear <= LATEST_INVENTORY_YEAR
        ? actualPerCapitaAtTargetYearTonnes
        : policyProjectedPerCapAtTarget;
    pctVsGoalAtTargetYear = pctActualVersusReference(actualPc, goalPerCapTonnes);
  } else if (!usesPerCapitaGoal && goalEmissionsMtOut != null) {
    const actualMt =
      targetYear != null && targetYear <= LATEST_INVENTORY_YEAR
        ? emissionsAtTargetYearMt
        : policyProjectedMtAtTargetYear;
    pctVsGoalAtTargetYear = pctActualVersusReference(actualMt, goalEmissionsMtOut);
  }

  const pctVsLinearAtLatestYear: number | null = null;

  return {
    capRow: cap,
    emissionsByYear: by,
    precalcYearMin,
    precalcYearMax,
    precalcYearCount,
    precalcSpanLabel,
    precalcDataAsOf,
    inventoryStatus,
    goalSummary,
    goalTonnesCo2ePerCapita: goalPerCapTonnes,
    usesPerCapitaGoal,
    goalEmissionsMt: goalEmissionsMtOut,
    baselineInventoryMt,
    emissionsAtTargetYearMt,
    targetMet,
    linearExpectedLatestMt,
    latestInventoryMt,
    onTrackLinearPath,
    linearGapMt,
    populationLatest,
    populationBaseline,
    populationGoalYear,
    perCapitaLatestTonnes,
    perCapitaBaselineTonnes,
    actualPerCapitaAtTargetYearTonnes,
    linearExpectedPerCapitaLatestTonnes,
    linearGapPerCapitaTonnes,
    pctVsGoalAtTargetYear,
    policyReductionGapPp,
    policyBaselineMtPolicySeries,
    policyProjectedMtAtTargetYear,
    policyAchievedReductionPct,
    capRequiredReductionPct,
    policyProjectedPerCapAtTarget,
    onTrackPolicyPath,
    pctVsLinearAtLatestYear,
    sectorsLatest,
    sectorsBaseline,
    phase2Flagged,
  };
}

/**
 * All jurisdictions with a CAP target year in california_cap_ghg_targets,
 * enriched with inventory comparison fields.
 */
export async function fetchCapTargetsProgressDetails(): Promise<CapTargetProgressDetail[]> {
  const caps = await fetchAllCapTargets();
  if (caps.length === 0) return [];

  const locationNames = caps.map((c) => String(c.location_name || '').trim()).filter(Boolean);
  const policyByLocation = await fetchPolicySeriesByLocation(locationNames);

  const gfList = caps.map((c) => Number(c.geofips)).filter((g) => Number.isFinite(g));
  const yearsNeeded = new Set<number>([LATEST_INVENTORY_YEAR]);
  for (const c of caps) {
    const by = c.baseline_year != null ? Math.round(Number(c.baseline_year)) : null;
    const ty = c.target_year != null ? Math.round(Number(c.target_year)) : null;
    if (by != null && Number.isFinite(by)) yearsNeeded.add(by);
    if (ty != null && Number.isFinite(ty)) yearsNeeded.add(ty);
  }

  const [precalc, popMap] = await Promise.all([
    fetchPrecalcForGeofipsList(gfList),
    fetchPopulationForGeofipsYears(gfList, Array.from(yearsNeeded)),
  ]);

  const { seriesByCanon, sectorsByCanonYear, precalcAsOfByCanon } = precalc;

  const details: CapTargetProgressDetail[] = [];
  for (const cap of caps) {
    const gf = Number(cap.geofips);
    const canon = canonicalInventoryGeofipsKey(gf);
    const series = seriesByCanon.get(canon) ?? {};
    const asOf = precalcAsOfByCanon.get(canon) ?? null;
    const pops = popMap.get(gf) ?? {};
    const sectors = sectorsByCanonYear.get(canon);
    const locKey = cap.location_name ? String(cap.location_name).trim() : '';
    const policyTs = locKey ? policyByLocation.get(locKey) ?? null : null;
    details.push(buildCapTargetProgressDetail(cap, series, asOf, pops, sectors, policyTs));
  }

  return details;
}

/** Escape CSV cell */
export function csvEscapeCell(val: string | number | boolean | null | undefined): string {
  if (val == null) return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Build CSV for export (filtered rows passed in). */
export function capTargetsProgressToCsv(rows: CapTargetProgressDetail[]): string {
  const headers = [
    'location_name',
    'geography_type',
    'county',
    'geofips',
    'target_year',
    'goal_summary',
    'goal_tonnes_co2e_per_capita',
    'uses_per_capita_goal',
    'target_source',
    'precalc_span',
    'coverage',
    'precalc_data_as_of',
    'population_latest_year',
    'population_baseline_year',
    'population_goal_year',
    'per_capita_tco2e_latest',
    'per_capita_tco2e_baseline_ref',
    'goal_mt',
    'actual_goal_year_mt',
    'actual_goal_year_t_per_cap',
    'target_met',
    'policy_baseline_mt_cap_year',
    'policy_projected_mt_target_year',
    'policy_achieved_reduction_pct',
    'cap_required_reduction_pct',
    'policy_reduction_gap_pp',
    'actual_latest_mt',
    'pct_vs_goal_at_target_year',
    'on_track_policy',
    'phase2_flagged',
    'sectors_latest_ldv_mt',
    'sectors_latest_hdv_mt',
    'sectors_latest_elec_mt',
    'sectors_latest_ng_mt',
    'sectors_latest_waste_mt',
    'sectors_latest_offroad_mt',
    'sectors_latest_ag_mt',
  ];
  const lines = [headers.join(',')];
  for (const d of rows) {
    const c = d.capRow;
    const s = d.sectorsLatest;
    lines.push(
      [
        csvEscapeCell(c.location_name),
        csvEscapeCell(c.geography_type),
        csvEscapeCell(c.county),
        csvEscapeCell(c.geofips),
        csvEscapeCell(c.target_year != null ? Math.round(Number(c.target_year)) : ''),
        csvEscapeCell(d.goalSummary),
        csvEscapeCell(d.goalTonnesCo2ePerCapita != null ? d.goalTonnesCo2ePerCapita.toFixed(4) : ''),
        csvEscapeCell(d.usesPerCapitaGoal ? 'yes' : 'no'),
        csvEscapeCell(c.target_source),
        csvEscapeCell(d.precalcSpanLabel),
        csvEscapeCell(d.inventoryStatus),
        csvEscapeCell(d.precalcDataAsOf),
        csvEscapeCell(d.populationLatest),
        csvEscapeCell(d.populationBaseline),
        csvEscapeCell(d.populationGoalYear),
        csvEscapeCell(d.perCapitaLatestTonnes != null ? d.perCapitaLatestTonnes.toFixed(3) : ''),
        csvEscapeCell(d.perCapitaBaselineTonnes != null ? d.perCapitaBaselineTonnes.toFixed(3) : ''),
        csvEscapeCell(d.goalEmissionsMt != null ? Math.round(d.goalEmissionsMt) : ''),
        csvEscapeCell(d.emissionsAtTargetYearMt != null ? Math.round(d.emissionsAtTargetYearMt) : ''),
        csvEscapeCell(
          d.actualPerCapitaAtTargetYearTonnes != null ? d.actualPerCapitaAtTargetYearTonnes.toFixed(4) : ''
        ),
        csvEscapeCell(
          d.targetMet === null ? '' : d.targetMet ? 'yes' : 'no'
        ),
        csvEscapeCell(d.policyBaselineMtPolicySeries != null ? Math.round(d.policyBaselineMtPolicySeries) : ''),
        csvEscapeCell(d.policyProjectedMtAtTargetYear != null ? Math.round(d.policyProjectedMtAtTargetYear) : ''),
        csvEscapeCell(d.policyAchievedReductionPct != null ? d.policyAchievedReductionPct.toFixed(2) : ''),
        csvEscapeCell(d.capRequiredReductionPct != null ? d.capRequiredReductionPct.toFixed(2) : ''),
        csvEscapeCell(d.policyReductionGapPp != null ? d.policyReductionGapPp.toFixed(2) : ''),
        csvEscapeCell(d.latestInventoryMt != null ? Math.round(d.latestInventoryMt) : ''),
        csvEscapeCell(d.pctVsGoalAtTargetYear != null ? d.pctVsGoalAtTargetYear.toFixed(2) : ''),
        csvEscapeCell(
          d.onTrackPolicyPath === null ? '' : d.onTrackPolicyPath ? 'yes' : 'no'
        ),
        csvEscapeCell(d.phase2Flagged ? 'yes' : 'no'),
        csvEscapeCell(s?.ldvMt != null ? Math.round(s.ldvMt) : ''),
        csvEscapeCell(s?.hdvMt != null ? Math.round(s.hdvMt) : ''),
        csvEscapeCell(s?.elecMt != null ? Math.round(s.elecMt) : ''),
        csvEscapeCell(s?.ngMt != null ? Math.round(s.ngMt) : ''),
        csvEscapeCell(s?.wasteMt != null ? Math.round(s.wasteMt) : ''),
        csvEscapeCell(s?.offroadMt != null ? Math.round(s.offroadMt) : ''),
        csvEscapeCell(s?.agMt != null ? Math.round(s.agMt) : ''),
      ].join(',')
    );
  }
  return lines.join('\r\n');
}
